import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  User,
  Mail,
  FileText,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Eye,
  Loader2,
  Upload,
  MessageSquare,
  Send,
  History,
  Clock,
  Shield,
  ShieldCheck,
  Timer,
  UserCheck,
  Zap,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';
import { DocumentViewer } from '../../components/admin/DocumentViewer';
import { KycTimeline, KycTimelineEvent } from '../../components/admin/KycTimeline';
import { KycStatusBadge } from '../../components/ui/KycStatusBadge';

interface KycAnnotation {
  id: string;
  text: string;
  createdAt: string;
  createdByName: string;
}

interface KycDocument {
  id: string;
  documentTypeCode: string;
  documentTypeName: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  status: string;
  rejectionReason?: string;
  uploadedAt: string;
  verifiedAt?: string;
  viewedAt?: string;
  viewedByName?: string;
  uploadedByAdmin?: boolean;
  annotations?: KycAnnotation[];
}

interface KycSubmission {
  id: string;
  kycType: string;
  status: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedByName?: string;
  rejectionReason?: string;
  additionalDocsReason?: string;
  documentsCount: number;
  documents: KycDocument[];
  // Two-level approval fields
  approvalLevel?: number; // 0 = not approved, 1 = first level, 2 = fully approved
  firstLevelApprovedAt?: string;
  firstLevelApprovedByName?: string;
  secondLevelApprovedAt?: string;
  secondLevelApprovedByName?: string;
  requiresTwoLevelApproval?: boolean;
  // SLA fields
  slaDeadline?: string;
  slaStatus?: 'ON_TIME' | 'WARNING' | 'BREACHED';
  slaRemainingHours?: number;
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  assignedAnalystId?: string;
  assignedAnalystName?: string;
}

interface KycDetails {
  virtualAccount: {
    id: string;
    userName: string;
    userEmail: string;
    userDocument?: string;
    pixKey?: string;
    status: string;
    contractStatus: string;
  };
  origin?: KycSubmission;
  destination?: KycSubmission;
}

// Status configuration for KYC states (used in TabButton component)
const _statusConfig: Record<string, { label: string; color: string; bgColor: string; icon: React.ElementType }> = {
  DRAFT: { label: 'Rascunho', color: 'text-surface-400', bgColor: 'bg-surface-500/20', icon: FileText },
  SUBMITTED: { label: 'Aguardando', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20', icon: Clock },
  PENDING_REVIEW: { label: 'Em Análise', color: 'text-blue-400', bgColor: 'bg-blue-500/20', icon: Clock },
  APPROVED: { label: 'Aprovado', color: 'text-green-400', bgColor: 'bg-green-500/20', icon: CheckCircle2 },
  REJECTED: { label: 'Rejeitado', color: 'text-red-400', bgColor: 'bg-red-500/20', icon: XCircle },
  ADDITIONAL_DOCS_REQUIRED: { label: 'Docs Adicionais', color: 'text-orange-400', bgColor: 'bg-orange-500/20', icon: AlertTriangle },
};
// Note: _statusConfig is available for future use in status display components

const SUPPORTED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function KycReview() {
  const { virtualAccountId } = useParams<{ virtualAccountId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [activeTab, setActiveTab] = useState<'ORIGIN' | 'DESTINATION'>('ORIGIN');
  const [viewingDocument, setViewingDocument] = useState<{ url: string; name: string; mimeType: string; isBlobUrl?: boolean } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [additionalDocsReason, setAdditionalDocsReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showAdditionalDocsModal, setShowAdditionalDocsModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showTimelinePanel, setShowTimelinePanel] = useState(false);
  const [annotationText, setAnnotationText] = useState('');
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Buscar detalhes do KYC
  const { data: kycDetails, isLoading } = useQuery<KycDetails>({
    queryKey: ['kyc-details', virtualAccountId],
    queryFn: async () => {
      const response = await api.get(`/admin/kyc/${virtualAccountId}`);
      return response.data;
    },
    enabled: !!virtualAccountId,
  });

  // Buscar timeline
  const { data: timeline } = useQuery<KycTimelineEvent[]>({
    queryKey: ['kyc-timeline', virtualAccountId],
    queryFn: async () => {
      const response = await api.get(`/admin/kyc/${virtualAccountId}/timeline`);
      return response.data.events || [];
    },
    enabled: !!virtualAccountId,
  });

  // Buscar templates de rejeição
  const { data: rejectionTemplates } = useQuery<Array<{ id: string; title: string; reason: string }>>({
    queryKey: ['kyc-rejection-templates'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/settings/rejection-templates');
      return response.data;
    },
  });

  // Mutation para aprovar
  const approveMutation = useMutation({
    mutationFn: async (kycType: string) => {
      const response = await api.post(`/admin/kyc/${virtualAccountId}/approve`, { kycType });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-details', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-timeline', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-pending'] });
      queryClient.invalidateQueries({ queryKey: ['kyc-stats'] });
    },
  });

  // Mutation para aprovação de primeiro nível
  const firstLevelApproveMutation = useMutation({
    mutationFn: async (kycType: string) => {
      const response = await api.post(`/admin/kyc/${virtualAccountId}/approve-first-level`, { kycType });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-details', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-timeline', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-pending'] });
      queryClient.invalidateQueries({ queryKey: ['kyc-stats'] });
    },
  });

  // Mutation para aprovação de segundo nível (supervisor)
  const secondLevelApproveMutation = useMutation({
    mutationFn: async (kycType: string) => {
      const response = await api.post(`/admin/kyc/${virtualAccountId}/approve-second-level`, { kycType });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-details', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-timeline', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-pending'] });
      queryClient.invalidateQueries({ queryKey: ['kyc-stats'] });
    },
  });

  // Mutation para rejeitar
  const rejectMutation = useMutation({
    mutationFn: async ({ kycType, reason }: { kycType: string; reason: string }) => {
      const response = await api.post(`/admin/kyc/${virtualAccountId}/reject`, { kycType, reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-details', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-timeline', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-pending'] });
      queryClient.invalidateQueries({ queryKey: ['kyc-stats'] });
      setShowRejectModal(false);
      setRejectReason('');
    },
  });

  // Mutation para solicitar documentos adicionais
  const requestDocsMutation = useMutation({
    mutationFn: async ({ kycType, reason }: { kycType: string; reason: string }) => {
      const response = await api.post(`/admin/kyc/${virtualAccountId}/request-documents`, { 
        kycType, 
        reason,
        requiredDocuments: [],
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-details', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-timeline', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-pending'] });
      setShowAdditionalDocsModal(false);
      setAdditionalDocsReason('');
    },
  });

  // Mutation para upload de documento pelo admin
  const uploadMutation = useMutation({
    mutationFn: async ({ file, documentType }: { file: File; documentType: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);
      formData.append('kycType', activeTab);
      
      const response = await api.post(
        `/admin/kyc/${virtualAccountId}/upload-document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-details', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-timeline', virtualAccountId] });
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadDocType('');
    },
  });

  // Mutation para adicionar anotação
  const addAnnotationMutation = useMutation({
    mutationFn: async ({ documentId, text }: { documentId: string; text: string }) => {
      const response = await api.post(
        `/admin/kyc/${virtualAccountId}/documents/${documentId}/annotation`,
        { text }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-details', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['kyc-timeline', virtualAccountId] });
      setAnnotationText('');
      setActiveDocumentId(null);
    },
  });

  // Mutation para marcar documento como visualizado
  const markViewedMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const response = await api.post(
        `/admin/kyc/${virtualAccountId}/documents/${documentId}/mark-viewed`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-details', virtualAccountId] });
    },
  });

  // Buscar URL do documento e marcar como visualizado
  const viewDocument = async (docId: string, docName: string, mimeType: string) => {
    try {
      // Marcar como visualizado
      markViewedMutation.mutate(docId);
      
      // Fetch document as blob with auth header
      const response = await api.get(`/admin/kyc/${virtualAccountId}/documents/${docId}/file`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(response.data);
      setViewingDocument({ url: blobUrl, name: docName, mimeType, isBlobUrl: true });
    } catch (error) {
      console.error('Erro ao buscar documento:', error);
    }
  };

  // Validar arquivo de upload
  const validateFile = (file: File): string | null => {
    if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
      return 'Tipo de arquivo não suportado. Use PDF, JPG ou PNG.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'Arquivo muito grande. Máximo 10MB.';
    }
    return null;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        alert(error);
        return;
      }
      setUploadFile(file);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!kycDetails) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-400">KYC não encontrado</p>
      </div>
    );
  }

  const currentSubmission = activeTab === 'ORIGIN' ? kycDetails.origin : kycDetails.destination;
  const currentStatus = currentSubmission?.status || 'DRAFT';
  const canReview = ['SUBMITTED', 'PENDING_REVIEW'].includes(currentStatus);

  return (
    <div className="flex gap-6">
      {/* Conteúdo Principal */}
      <div className="flex-1 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/kyc')}
              className="p-2 text-surface-400 hover:text-surface-100 hover:bg-surface-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-surface-100">Análise de KYC</h1>
              <p className="text-surface-400 mt-1">
                Revise os documentos e aprove ou rejeite a verificação
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowTimelinePanel(!showTimelinePanel)}
            className={cn(
              "p-2 rounded-lg transition-colors flex items-center gap-2",
              showTimelinePanel 
                ? "bg-primary-500 text-white" 
                : "text-surface-400 hover:text-surface-100 hover:bg-surface-800"
            )}
          >
            <History className="w-5 h-5" />
            <span className="hidden md:inline">Timeline</span>
          </button>
        </div>

        {/* Dados do Correntista */}
        <div className="glass rounded-xl p-6">
          <h2 className="text-lg font-semibold text-surface-100 mb-4">Dados do Correntista</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-surface-800 flex items-center justify-center">
                <User className="w-5 h-5 text-surface-400" />
              </div>
              <div>
                <p className="text-sm text-surface-500">Nome</p>
                <p className="font-medium text-surface-100">{kycDetails.virtualAccount.userName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-surface-800 flex items-center justify-center">
                <Mail className="w-5 h-5 text-surface-400" />
              </div>
              <div>
                <p className="text-sm text-surface-500">Email</p>
                <p className="font-medium text-surface-100">{kycDetails.virtualAccount.userEmail}</p>
              </div>
            </div>
            {kycDetails.virtualAccount.userDocument && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-surface-800 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-surface-400" />
                </div>
                <div>
                  <p className="text-sm text-surface-500">Documento</p>
                  <p className="font-medium text-surface-100">{kycDetails.virtualAccount.userDocument}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <TabButton
            active={activeTab === 'ORIGIN'}
            onClick={() => setActiveTab('ORIGIN')}
            label="KYC de Origem"
            status={kycDetails.origin?.status}
          />
          <TabButton
            active={activeTab === 'DESTINATION'}
            onClick={() => setActiveTab('DESTINATION')}
            label="KYC de Destino"
            status={kycDetails.destination?.status}
          />
        </div>

        {/* Conteúdo do KYC */}
        {currentSubmission ? (
          <div className="space-y-6">
            {/* Status */}
            <div className="glass rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <KycStatusBadge 
                    status={currentStatus === 'SUBMITTED' ? 'PENDING' : currentStatus === 'PENDING_REVIEW' ? 'IN_REVIEW' : currentStatus} 
                    size="lg"
                  />
                </div>
                {currentSubmission.submittedAt && (
                  <div className="text-right">
                    <p className="text-sm text-surface-500">Enviado em</p>
                    <p className="text-surface-300">
                      {new Date(currentSubmission.submittedAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Documentos */}
            <div className="glass rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-surface-100">
                  Documentos ({currentSubmission.documentsCount})
                </h2>
                {canReview && (
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="px-3 py-1.5 bg-surface-800 text-surface-300 rounded-lg hover:bg-surface-700 transition-colors flex items-center gap-2 text-sm"
                  >
                    <Upload className="w-4 h-4" />
                    Enviar Documento
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {currentSubmission.documents.map((doc) => (
                  <DocumentCard
                    key={doc.id}
                    document={doc}
                    onView={() => viewDocument(doc.id, doc.originalName, doc.mimeType)}
                    onAddAnnotation={() => setActiveDocumentId(doc.id)}
                    showAnnotations={activeDocumentId === doc.id}
                    annotationText={annotationText}
                    onAnnotationChange={setAnnotationText}
                    onSubmitAnnotation={() => {
                      if (annotationText.trim()) {
                        addAnnotationMutation.mutate({ documentId: doc.id, text: annotationText });
                      }
                    }}
                    isSubmittingAnnotation={addAnnotationMutation.isPending}
                  />
                ))}
              </div>
            </div>

            {/* Ações */}
            {canReview && (
              <div className="glass rounded-xl p-6">
                <h2 className="text-lg font-semibold text-surface-100 mb-4">Ações</h2>
                
                {/* SLA and Priority Info */}
                {(currentSubmission.slaStatus || currentSubmission.priority) && (
                  <div className="flex flex-wrap gap-3 mb-4">
                    {currentSubmission.slaStatus && (
                      <div className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg border",
                        currentSubmission.slaStatus === 'ON_TIME' && "bg-green-500/10 border-green-500/30 text-green-400",
                        currentSubmission.slaStatus === 'WARNING' && "bg-yellow-500/10 border-yellow-500/30 text-yellow-400",
                        currentSubmission.slaStatus === 'BREACHED' && "bg-red-500/10 border-red-500/30 text-red-400"
                      )}>
                        <Timer className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {currentSubmission.slaStatus === 'ON_TIME' && 'SLA no prazo'}
                          {currentSubmission.slaStatus === 'WARNING' && 'SLA em alerta'}
                          {currentSubmission.slaStatus === 'BREACHED' && 'SLA estourado'}
                        </span>
                        {currentSubmission.slaRemainingHours !== undefined && currentSubmission.slaStatus !== 'BREACHED' && (
                          <span className="text-xs opacity-75">({currentSubmission.slaRemainingHours}h restantes)</span>
                        )}
                      </div>
                    )}
                    {currentSubmission.priority && currentSubmission.priority !== 'NORMAL' && (
                      <div className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg border",
                        currentSubmission.priority === 'URGENT' && "bg-red-500/10 border-red-500/30 text-red-400",
                        currentSubmission.priority === 'HIGH' && "bg-orange-500/10 border-orange-500/30 text-orange-400",
                        currentSubmission.priority === 'LOW' && "bg-surface-500/10 border-surface-500/30 text-surface-400"
                      )}>
                        <Zap className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Prioridade {currentSubmission.priority === 'URGENT' ? 'Urgente' : currentSubmission.priority === 'HIGH' ? 'Alta' : 'Baixa'}
                        </span>
                      </div>
                    )}
                    {currentSubmission.assignedAnalystName && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-primary-500/10 border-primary-500/30 text-primary-400">
                        <UserCheck className="w-4 h-4" />
                        <span className="text-sm">Analista: {currentSubmission.assignedAnalystName}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Two-Level Approval Indicator */}
                {currentSubmission.requiresTwoLevelApproval && (
                  <div className="mb-4 p-4 bg-surface-800 rounded-lg border border-surface-700">
                    <h3 className="text-sm font-medium text-surface-300 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-primary-400" />
                      Aprovação em Dois Níveis
                    </h3>
                    <div className="flex items-center gap-4">
                      {/* First Level */}
                      <div className={cn(
                        "flex-1 p-3 rounded-lg border-2 transition-all",
                        currentSubmission.approvalLevel && currentSubmission.approvalLevel >= 1
                          ? "bg-green-500/10 border-green-500/50"
                          : "bg-surface-700 border-surface-600"
                      )}>
                        <div className="flex items-center gap-2 mb-1">
                          {currentSubmission.approvalLevel && currentSubmission.approvalLevel >= 1 ? (
                            <CheckCircle2 className="w-5 h-5 text-green-400" />
                          ) : (
                            <Clock className="w-5 h-5 text-surface-400" />
                          )}
                          <span className={cn(
                            "font-medium",
                            currentSubmission.approvalLevel && currentSubmission.approvalLevel >= 1
                              ? "text-green-400"
                              : "text-surface-400"
                          )}>
                            1º Nível (Analista)
                          </span>
                        </div>
                        {currentSubmission.firstLevelApprovedAt && (
                          <p className="text-xs text-surface-500">
                            {currentSubmission.firstLevelApprovedByName} • {new Date(currentSubmission.firstLevelApprovedAt).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        )}
                      </div>

                      {/* Arrow */}
                      <div className="text-surface-500">→</div>

                      {/* Second Level */}
                      <div className={cn(
                        "flex-1 p-3 rounded-lg border-2 transition-all",
                        currentSubmission.approvalLevel === 2
                          ? "bg-green-500/10 border-green-500/50"
                          : "bg-surface-700 border-surface-600"
                      )}>
                        <div className="flex items-center gap-2 mb-1">
                          {currentSubmission.approvalLevel === 2 ? (
                            <ShieldCheck className="w-5 h-5 text-green-400" />
                          ) : (
                            <Clock className="w-5 h-5 text-surface-400" />
                          )}
                          <span className={cn(
                            "font-medium",
                            currentSubmission.approvalLevel === 2
                              ? "text-green-400"
                              : "text-surface-400"
                          )}>
                            2º Nível (Supervisor)
                          </span>
                        </div>
                        {currentSubmission.secondLevelApprovedAt && (
                          <p className="text-xs text-surface-500">
                            {currentSubmission.secondLevelApprovedByName} • {new Date(currentSubmission.secondLevelApprovedAt).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  {/* Two-Level Approval Buttons */}
                  {currentSubmission.requiresTwoLevelApproval ? (
                    <>
                      {/* First Level Approval Button */}
                      {(!currentSubmission.approvalLevel || currentSubmission.approvalLevel === 0) && (
                        <button
                          onClick={() => firstLevelApproveMutation.mutate(activeTab)}
                          disabled={firstLevelApproveMutation.isPending}
                          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                          {firstLevelApproveMutation.isPending ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Shield className="w-5 h-5" />
                          )}
                          Aprovar 1º Nível
                        </button>
                      )}

                      {/* Second Level Approval Button */}
                      {currentSubmission.approvalLevel === 1 && (
                        <button
                          onClick={() => secondLevelApproveMutation.mutate(activeTab)}
                          disabled={secondLevelApproveMutation.isPending}
                          className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                          {secondLevelApproveMutation.isPending ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <ShieldCheck className="w-5 h-5" />
                          )}
                          Aprovar 2º Nível (Final)
                        </button>
                      )}

                      {/* Already fully approved */}
                      {currentSubmission.approvalLevel === 2 && (
                        <div className="px-6 py-3 bg-green-500/20 text-green-400 rounded-lg flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5" />
                          Totalmente Aprovado
                        </div>
                      )}
                    </>
                  ) : (
                    /* Single Level Approval Button */
                    <button
                      onClick={() => approveMutation.mutate(activeTab)}
                      disabled={approveMutation.isPending}
                      className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {approveMutation.isPending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-5 h-5" />
                      )}
                      Aprovar KYC
                    </button>
                  )}

                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                  >
                    <XCircle className="w-5 h-5" />
                    Rejeitar KYC
                  </button>
                  <button
                    onClick={() => setShowAdditionalDocsModal(true)}
                    className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                  >
                    <AlertTriangle className="w-5 h-5" />
                    Solicitar Documentos
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="glass rounded-xl p-8 text-center">
            <FileText className="w-12 h-12 text-surface-500 mx-auto mb-3" />
            <p className="text-surface-400">Nenhum documento enviado para este tipo de KYC</p>
          </div>
        )}
      </div>

      {/* Painel de Timeline */}
      {showTimelinePanel && (
        <div className="w-80 flex-shrink-0">
          <div className="glass rounded-xl p-4 sticky top-4">
            <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-primary-400" />
              Timeline
            </h3>
            <KycTimeline events={timeline || []} />
          </div>
        </div>
      )}

      {/* Modal de Visualização de Documento */}
      {viewingDocument && (
        <DocumentViewer
          documentUrl={viewingDocument.url}
          documentName={viewingDocument.name}
          mimeType={viewingDocument.mimeType}
          onClose={() => {
            if (viewingDocument?.isBlobUrl) {
              URL.revokeObjectURL(viewingDocument.url);
            }
            setViewingDocument(null);
          }}
          onError={(error) => console.error('Erro ao visualizar documento:', error)}
        />
      )}

      {/* Modal de Rejeição */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">Rejeitar KYC</h3>
            <p className="text-surface-400 mb-4">
              Informe o motivo da rejeição. O correntista será notificado e poderá reenviar os documentos.
            </p>

            {/* Rejection Template Dropdown */}
            {rejectionTemplates && rejectionTemplates.length > 0 && (
              <div className="mb-3">
                <label className="block text-sm text-surface-400 mb-1">Template de Rejeição</label>
                <select
                  onChange={(e) => {
                    const template = rejectionTemplates.find((t) => t.id === e.target.value);
                    if (template) {
                      setRejectReason(template.reason);
                    }
                  }}
                  className="w-full p-3 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
                  defaultValue=""
                >
                  <option value="" disabled>Selecione um template (opcional)</option>
                  {rejectionTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Motivo da rejeição (mínimo 10 caracteres)"
              className="w-full p-3 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-primary-500 resize-none h-32"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-surface-400 hover:text-surface-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => rejectMutation.mutate({ kycType: activeTab, reason: rejectReason })}
                disabled={rejectReason.length < 10 || rejectMutation.isPending}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {rejectMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Documentos Adicionais */}
      {showAdditionalDocsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">Solicitar Documentos Adicionais</h3>
            <p className="text-surface-400 mb-4">
              Informe quais documentos adicionais são necessários. O correntista será notificado.
            </p>
            <textarea
              value={additionalDocsReason}
              onChange={(e) => setAdditionalDocsReason(e.target.value)}
              placeholder="Descreva os documentos necessários (mínimo 10 caracteres)"
              className="w-full p-3 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-primary-500 resize-none h-32"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowAdditionalDocsModal(false)}
                className="px-4 py-2 text-surface-400 hover:text-surface-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => requestDocsMutation.mutate({ kycType: activeTab, reason: additionalDocsReason })}
                disabled={additionalDocsReason.length < 10 || requestDocsMutation.isPending}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {requestDocsMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Solicitar Documentos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Upload */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">
              Enviar Documento para o Correntista
            </h3>
            <p className="text-surface-400 mb-4">
              Envie um documento em nome do correntista. Isso será registrado na timeline.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-surface-400 mb-1">Tipo de Documento</label>
                <select
                  value={uploadDocType}
                  onChange={(e) => setUploadDocType(e.target.value)}
                  className="w-full p-3 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
                >
                  <option value="">Selecione o tipo</option>
                  <option value="RG_FRENTE">RG - Frente</option>
                  <option value="RG_VERSO">RG - Verso</option>
                  <option value="CNH_FRENTE">CNH - Frente</option>
                  <option value="CNH_VERSO">CNH - Verso</option>
                  <option value="COMPROVANTE_RESIDENCIA">Comprovante de Residência</option>
                  <option value="SELFIE">Selfie com Documento</option>
                  <option value="OUTRO">Outro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-surface-400 mb-1">Arquivo</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full p-4 border-2 border-dashed border-surface-700 rounded-lg text-surface-400 hover:border-primary-500 hover:text-primary-400 transition-colors"
                >
                  {uploadFile ? (
                    <span className="text-surface-100">{uploadFile.name}</span>
                  ) : (
                    <span>Clique para selecionar (PDF, JPG, PNG - máx 10MB)</span>
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setUploadDocType('');
                }}
                className="px-4 py-2 text-surface-400 hover:text-surface-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (uploadFile && uploadDocType) {
                    uploadMutation.mutate({ file: uploadFile, documentType: uploadDocType });
                  }
                }}
                disabled={!uploadFile || !uploadDocType || uploadMutation.isPending}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {uploadMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Enviar Documento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
  status,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  status?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2",
        active
          ? "bg-primary-500 text-white"
          : "bg-surface-800 text-surface-400 hover:text-surface-100"
      )}
    >
      {label}
      {status && (
        <KycStatusBadge 
          status={status === 'SUBMITTED' ? 'PENDING' : status === 'PENDING_REVIEW' ? 'IN_REVIEW' : status} 
          size="sm" 
          showLabel={false}
        />
      )}
    </button>
  );
}

function DocumentCard({
  document,
  onView,
  onAddAnnotation,
  showAnnotations,
  annotationText,
  onAnnotationChange,
  onSubmitAnnotation,
  isSubmittingAnnotation,
}: {
  document: KycDocument;
  onView: () => void;
  onAddAnnotation: () => void;
  showAnnotations: boolean;
  annotationText: string;
  onAnnotationChange: (text: string) => void;
  onSubmitAnnotation: () => void;
  isSubmittingAnnotation: boolean;
}) {
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="bg-surface-800 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-surface-700 flex items-center justify-center">
            <FileText className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-surface-100">{document.documentTypeName}</p>
              {document.uploadedByAdmin && (
                <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded">
                  Admin
                </span>
              )}
              {document.viewedAt && (
                <span className="px-1.5 py-0.5 bg-surface-700 text-surface-400 text-xs rounded flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  Visto
                </span>
              )}
            </div>
            <p className="text-sm text-surface-500">
              {document.originalName} • {formatFileSize(document.fileSize)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {document.status === 'VERIFIED' && (
            <CheckCircle2 className="w-5 h-5 text-green-400" />
          )}
          {document.status === 'REJECTED' && (
            <XCircle className="w-5 h-5 text-red-400" />
          )}
          <button
            onClick={onAddAnnotation}
            className={cn(
              "p-2 rounded-lg transition-colors",
              showAnnotations 
                ? "bg-purple-500/20 text-purple-400" 
                : "text-surface-400 hover:text-purple-400 hover:bg-surface-700"
            )}
            title="Anotações"
          >
            <MessageSquare className="w-5 h-5" />
          </button>
          <button
            onClick={onView}
            className="p-2 text-surface-400 hover:text-primary-400 hover:bg-surface-700 rounded-lg transition-colors"
            title="Visualizar"
          >
            <Eye className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Seção de Anotações */}
      {showAnnotations && (
        <div className="border-t border-surface-700 p-4 bg-surface-800/50">
          <h4 className="text-sm font-medium text-surface-300 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-purple-400" />
            Anotações
          </h4>
          
          {/* Lista de anotações existentes */}
          {document.annotations && document.annotations.length > 0 ? (
            <div className="space-y-2 mb-3">
              {document.annotations.map((annotation) => (
                <div key={annotation.id} className="p-2 bg-surface-700 rounded-lg">
                  <p className="text-sm text-surface-200">{annotation.text}</p>
                  <p className="text-xs text-surface-500 mt-1">
                    {annotation.createdByName} • {new Date(annotation.createdAt).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-surface-500 mb-3">Nenhuma anotação ainda.</p>
          )}

          {/* Formulário para nova anotação */}
          <div className="flex gap-2">
            <input
              type="text"
              value={annotationText}
              onChange={(e) => onAnnotationChange(e.target.value)}
              placeholder="Adicionar anotação..."
              className="flex-1 px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-primary-500 text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && annotationText.trim()) {
                  onSubmitAnnotation();
                }
              }}
            />
            <button
              onClick={onSubmitAnnotation}
              disabled={!annotationText.trim() || isSubmittingAnnotation}
              className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50"
            >
              {isSubmittingAnnotation ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default KycReview;
